<script setup lang="ts">
import { Button, I18n, Loader, useTranslation } from '@bluebrick/saas-ui'
import { isAxiosError, HttpStatusCode } from 'axios'
import { storeToRefs } from 'pinia'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { type ElementRef } from '@/common/utils'
import { useActionsStore } from '@/features/actions/useActionsStore'
import { useApi } from '@/features/api/useAPI'
import { useAppStore } from '@/features/app/useAppStore'
import EventsSyncModal from '@/features/conflicts/components/EventsSyncModal.vue'
import { useDataHealthStore } from '@/features/dataHealth/useDataHealthStore'
import DiscountSettersModal from '@/features/discountSetters/components/DiscountSetterModal.vue'
import { useDiscountSettersStore } from '@/features/discountSetters/useDiscountSettersStore'
import DeleteEventModalWindow from '@/features/eventDetails/components/DeleteEventModalWindow.vue'
import { useEventsStore } from '@/features/events/useEventsStore'
import useIFrameIncomingMsg from '@/features/iframe/useIFrameIncomingMsg'
import { emitToParent } from '@/features/iframe/useParentEmit'
import NavigationBar from '@/features/navigation/components/NavigationBar.vue'
import Notifications from '@/features/notifications/components/Notifications.vue'
import { NewDataNotificationId } from '@/features/notifications/types'
import { useNotificationsStore } from '@/features/notifications/useNotificationsStore'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useSaleDaysSummaryStore } from '@/features/saleDaysSummary/useSaleDaysSummaryStore'
import SidePanel from '@/features/sidePanel/components/SidePanel.vue'
import { useSidebarStore } from '@/features/sidePanel/useSidebarStore'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import NoStoresPlaceholder from '@/features/stores/components/NoStoresPlaceholder.vue'
import StoreContainer from '@/features/stores/components/StoreContainer.vue'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { type TelemetryTransaction } from '@/features/telemetry/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import UpsellModal from '@/features/upsell/component/UpsellModal.vue'
import { useUpsellStore } from '@/features/upsell/useUpsellStore'
import { useUserStore } from '@/features/user/useUserStore'
import { PATH } from '@/routes'

const { setDefaultDatesRange } = useCalendarStore()
const { isEmbedded, isIdle } = storeToRefs(useAppStore())
const { fetch: fetchStores, toggleStoreContainerExpandedState } = useStoresStore()
const { stores } = storeToRefs(useStoresStore())
const { fetch: fetchSaleDaysSummary } = useSaleDaysSummaryStore()
const { fetch: fetchActions, indicatePendingRefresh } = useActionsStore()
const { fetch: fetchUpsellData, openPlatformsUpsellModal } = useUpsellStore()
const { shouldShowUpsellModal, upsellModalType } = storeToRefs(useUpsellStore())
const { loadAllSKUs, loadFavouriteSKUIds, setDefaultFavouriteSKUs } = useSkusStore()
const { trackEvent, setInstallationId, startTransaction } = useTelemetry()
const { setDashboardDiscountAccess, showAdvisedActions } = useSidebarStore()
const { initUser, setUserToken } = useUserStore()
const { loadAllStoresData } = useEventsStore()
const { t } = useTranslation()
const { addResponseInterceptor, removeResponseInterceptor } = useApi()
const router = useRouter()
const { isNewerDataNotificationVisible } = storeToRefs(useDataHealthStore())
const { notifyNewData } = useNotificationsStore()
const { shouldShowDiscountSettersModal } = storeToRefs(useDiscountSettersStore())

const preferencesStore = usePreferencesStore()

const { checkDontShowConflictsModalAgain } = preferencesStore

const { didCheckDontShowConflictsModalAgain } = storeToRefs(preferencesStore)

const eventPlanner = ref<ElementRef>(null)

defineProps<{
	configurableResolution?: boolean
}>()

const {
	updateLastScrapeDates,
	start: startPollingLastScrapeDates,
	stop: stopPollingLastScrapeDates,
	updatePortalsStatus,
	fetch: fetchDiscountsLastScrapeDates
} = useDataHealthStore()
const { hasAnyStoresWithDiscountsScraped } = storeToRefs(useDataHealthStore())
const { updateSourceAccounts } = useDiscountSettersStore()

useIFrameIncomingMsg({
	DASHBOARD_DISCOUNT_ANALYTICS_ENABLED: setDashboardDiscountAccess,
	INSTALLATION_ID_UPDATED: setInstallationId,
	TOKEN_HAS_CHANGED: (payload) => {
		setUserToken(payload)
	},
	SCRAPERS_STATUS: (payload) => {
		trackEvent('app', 'Received new scrapers status')

		updatePortalsStatus(payload)
	},
	LAST_SCRAPE_DATES_CHANGED: (payload) => {
		trackEvent('app', 'Received new last scrape dates', {
			data: JSON.stringify(payload)
		})

		updateLastScrapeDates(payload)

		// If we receive this message at least once then it means that the container app (in embedded mode)
		// does data polling for us and we don't have to do it ourselves
		stopPollingLastScrapeDates(true)
	},
	SHOULD_OPEN_ACTIONS_PANEL: () => {
		showAdvisedActions()
	},
	IDLE_STATE_CHANGED: (payload) => {
		trackEvent('app', 'App idle state changed', {
			data: JSON.stringify(payload)
		})

		isIdle.value = payload
	},
	SOURCE_ACCOUNTS: (payload) => {
		updateSourceAccounts(payload)
	}
})

const isInitializing = ref(true)
const initializationStage = ref('')

async function initStores(transaction: TelemetryTransaction | undefined) {
	const span = transaction?.startSpan('fetchStores', 'initialization')

	await fetchStores()

	if (stores.value.length > 0) {
		// Make the container of first store expanded by default
		toggleStoreContainerExpandedState(stores.value[0].id, true)

		await fetchDiscountsLastScrapeDates()
	}

	span?.end()
}

async function initSKUs(transaction: TelemetryTransaction | undefined) {
	const span = transaction?.startSpan('fetchSKUs', 'initialization')

	await loadAllSKUs()

	span?.end()
}

async function initFavouriteSKUs(transaction: TelemetryTransaction | undefined) {
	const span = transaction?.startSpan('fetchFavouriteSKUs', 'initialization')

	await loadFavouriteSKUIds()

	span?.end()
}

async function loadEvents(transaction: TelemetryTransaction | undefined) {
	const span = transaction?.startSpan('loadingEvents', 'initialization')

	await loadAllStoresData()

	span?.end()
}

async function initData(transaction: TelemetryTransaction | undefined) {
	initializationStage.value = t('initialization.stores')
	await initStores(transaction)

	if (!hasAnyStoresWithDiscountsScraped.value) {
		trackEvent('app', 'Skipping most data initialization as no discount scrapers are present')

		return
	}

	initializationStage.value = t('initialization.skus')
	await initSKUs(transaction)

	initializationStage.value = t('initialization.favouriteSKUs')
	await setDefaultFavouriteSKUs()
	await initFavouriteSKUs(transaction)

	initializationStage.value = t('initialization.events')
	await loadEvents(transaction)
}

function loadSaleDaysSummary() {
	// No "async/await" here on purpose as we don't need/want to wait for this part to be loaded before proceeding
	fetchSaleDaysSummary()
}

async function loadData(transaction: TelemetryTransaction | undefined) {
	isInitializing.value = true

	indicatePendingRefresh()

	await initData(transaction)

	if (hasAnyStoresWithDiscountsScraped.value) {
		loadSaleDaysSummary()
		fetchActions()
		fetchUpsellData()
	}
}

async function reloadData() {
	const transaction = startTransaction('EventsPlanner', 'reloading')

	isInitializing.value = true

	stopPollingLastScrapeDates()

	await initUser(transaction)

	await loadData(transaction)

	startPollingLastScrapeDates()

	isInitializing.value = false

	transaction?.end()
}

function onNotificationAction(name: string) {
	if (!isInitializing.value && name === NewDataNotificationId) {
		trackEvent('app', 'Reloading after new data detected')

		reloadData()
	}
}

let apiResponseInterceptorId: number

onMounted(async () => {
	emitToParent('LOADED', import.meta.env.VITE_APP_VERSION)

	const transaction = startTransaction('EventsPlanner', 'initialization')

	await loadData(transaction)

	transaction?.end()

	setDefaultDatesRange()

	startPollingLastScrapeDates()

	isInitializing.value = false

	trackEvent('app', 'Events-planner loaded')

	apiResponseInterceptorId = addResponseInterceptor(undefined, (error) => {
		if (!isEmbedded.value && isAxiosError(error) && error.status === HttpStatusCode.Unauthorized) {
			// We do not manage auth token in non-embedded mode, so if current token expires then we
			// just redirect to the login page
			router.push({
				path: PATH.LOGIN
			})
		}

		return Promise.reject(error)
	})
})

onBeforeUnmount(() => {
	removeResponseInterceptor(apiResponseInterceptorId)
})

// TODO: Disabled for now - add real condition for `shouldShowEventsSyncModal` (https://indiebi.atlassian.net/browse/DA-135)
const shouldShowEventsSyncModal = ref(Math.random() > 5 && !didCheckDontShowConflictsModalAgain.value)

function eventsSyncContinue(hasCheckedDontShowAgain: boolean) {
	shouldShowEventsSyncModal.value = false

	if (hasCheckedDontShowAgain) {
		checkDontShowConflictsModalAgain()
	}
}

watch(isNewerDataNotificationVisible, (current, previous) => {
	if (!isInitializing.value && !!current && !previous) {
		if (isEmbedded.value && isIdle.value) {
			reloadData()
		} else {
			notifyNewData()
		}
	}
})
</script>

<template>
	<SidePanel />
	<div ref="eventPlanner" :class="['planner', { 'planner--embedded': isEmbedded }]">
		<NavigationBar
			class="mb-05"
			:is-loading="isInitializing"
			:should-show-placeholder="isInitializing || !hasAnyStoresWithDiscountsScraped"
			@reload="reloadData()"
		/>

		<div v-if="isInitializing" class="mt-06 flex items-start justify-center">
			<div class="flex items-center gap-05 text-ink-60">
				<span>{{ initializationStage }}</span>
				<Loader size="big" />
			</div>
		</div>

		<div v-else-if="hasAnyStoresWithDiscountsScraped" class="stores mx-auto">
			<div v-for="store in stores" :key="store.id">
				<StoreContainer :store="store" :is-initializing="isInitializing" @reload="reloadData" />
			</div>

			<div class="platformsUpsell font-body-s mt-06 flex items-center justify-center gap-05 rounded-sm py-04">
				<I18n path="upsell.platforms.desc" />
				<Button size="xs" variant="secondary" @click="openPlatformsUpsellModal"><I18n path="upsell.platforms.cta" /></Button>
			</div>
		</div>

		<div v-else>
			<NoStoresPlaceholder @reload="reloadData" />
		</div>
	</div>

	<DeleteEventModalWindow />
	<EventsSyncModal v-if="shouldShowEventsSyncModal" @continue="eventsSyncContinue" />
	<UpsellModal v-if="shouldShowUpsellModal" :type="upsellModalType" @close="shouldShowUpsellModal = false" />
	<DiscountSettersModal v-if="shouldShowDiscountSettersModal" />
	<Notifications @action="onNotificationAction" />
</template>

<style scoped lang="scss">
.planner {
	display: grid;
	grid-template-rows: auto 1fr;
	height: 100vh;
	max-width: 160rem;
	padding: 1.5rem 2rem;
	margin: 0 auto;
}

.planner--embedded {
	padding: 1.5rem 2rem;
}

.stores {
	overflow-y: auto;
	width: 100%;
}

::-webkit-scrollbar {
	width: 1rem !important;
	height: 1rem !important;
}

::-webkit-scrollbar-thumb {
	border-left: 0.5rem solid theme('colors.level.00');
}

.platformsUpsell {
	border: 0.0625rem solid theme('colors.ink.12');
}
</style>
